// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { createBundle } from '@/bundle/api/create-bundle';
import {
  BundleMembersForm,
  BundleMembersFormRef,
} from '@/bundle/components/bundle-members-form';
import { bundlesQueryKey } from '@/bundle/hooks/use-bundles';
import { bundleMembersFormSchema } from '@/bundle/schemas/bundle-members';
import { isDuplicateBundleNameError } from '@/bundle/utilities/duplicate-bundle-name-error';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { nullifyEmptyString } from '@/utilities/nullify-empty-string';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const FORM_ID = 'create-bundle-form';

// How long the created toast stays visible, in milliseconds
const CREATED_TOAST_DURATION_MS = 5000;

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  members: bundleMembersFormSchema,
});

// Member rows start with a null item in form state; the schema's refine
// guarantees every row has an item by the time the output type is produced.
type FormInputData = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

function getDefaultValues(initialName?: string): FormInputData {
  return {
    name: initialName ?? '',
    description: '',
    members: [{ item: null, quantity: 1 }],
  };
}

interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog should close.
   */
  onClose: () => void;

  /**
   * Optional initial name used to pre-populate the name field.
   */
  initialName?: string;
}

/**
 * Dialog for creating a new bundle in the catalog.
 *
 * Captures the complete bundle in one step: name, optional description,
 * and an ordered member list with default quantities. A bundle can never
 * be created empty, so submit is blocked with an inline error until at
 * least one row has an item selected.
 */
export function CreateBundleDialog(props: Props) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const membersFormRef = useRef<BundleMembersFormRef>(null);

  const methods = useForm<FormInputData, unknown, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(props.initialName),
  });

  const { control, handleSubmit, reset, setError, formState } = methods;

  // Re-apply the initial name on every open, not just mount; the exited
  // reset clears it, so a reopen with the name still set needs this
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    reset(getDefaultValues(props.initialName));
  }, [props.isOpen, props.initialName, reset]);

  const createBundleMutation = useMutation({
    mutationFn: createBundle,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: bundlesQueryKey });

      showToast({
        severity: 'success',
        duration: CREATED_TOAST_DURATION_MS,
        element: (
          <span>
            Bundle{' '}
            <RouterLink
              to="/catalog/bundles/$bundleKey"
              params={{ bundleKey: result.bundle.key }}
            >
              <MuiLink
                component="span"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                {result.bundle.name}
              </MuiLink>
            </RouterLink>{' '}
            created
          </span>
        ),
      });

      props.onClose();
    },
    onError: (error) => {
      // The duplicate-name case is a handled validation error with an inline
      // message; anything else is unexpected and must reach Sentry alongside
      // the alert (4xx responses are not auto-captured)
      if (isDuplicateBundleNameError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A bundle with this name already exists',
        });
        return;
      }
      if (!handleAPIError(error).sentToSentry) {
        captureException(error);
      }
    },
  });

  const isAlertOpen =
    createBundleMutation.isError &&
    !isDuplicateBundleNameError(createBundleMutation.error);

  const handleClose = () => {
    if (createBundleMutation.isPending) {
      return;
    }
    props.onClose();
  };

  const handleExited = () => {
    reset(getDefaultValues());
    createBundleMutation.reset();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    // Array order is the persisted member order
    const members = data.members.map((member) => ({
      itemId: member.item.id,
      defaultQty: member.quantity,
    }));

    createBundleMutation.mutate({
      name: data.name,
      description: nullifyEmptyString(data.description ?? null),
      members,
    });
  };

  const membersError =
    formState.errors.members?.root?.message ??
    formState.errors.members?.message;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleExited,
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while creating the bundle."
        onClose={createBundleMutation.reset}
      />

      <DialogTitle>Add bundle</DialogTitle>
      <DialogContent dividers>
        <FormProvider {...methods}>
          <form id={FORM_ID} onSubmit={handleSubmit(handleValidSubmission)}>
            <Stack spacing={2} paddingTop={1}>
              <FormTextField name="name" label="Name" control={control} />
              <FormTextField
                name="description"
                label="Description"
                control={control}
                isMultiline
                minimumRows={2}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">Items</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => membersFormRef.current?.addMemberRow()}
                  startIcon={<AddRounded fontSize="small" />}
                >
                  Add item
                </Button>
              </Stack>
              <BundleMembersForm ref={membersFormRef} name="members" />
              {membersError && (
                <FormHelperText error>{membersError}</FormHelperText>
              )}
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={handleClose}
          disabled={createBundleMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={createBundleMutation.isPending}
          startIcon={
            createBundleMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
